/**
 * Platform detection helpers for the install flow.
 *
 * iOS PWAs are subtle:
 *   - Only Safari can "Add to Home Screen" — Chrome/Firefox/Edge on iOS are
 *     skinned-WebKit shells with no install API and no Share→AddToHomeScreen.
 *   - iPadOS 13+ identifies as Mac in the UA; we catch it via maxTouchPoints.
 *   - In-app browsers (Instagram, Facebook, X, etc.) cannot install at all.
 *
 * Android Chrome / Edge fire `beforeinstallprompt`; the caller listens for it
 * separately and only invokes our flow when no prompt is available.
 */

export function detectPlatform() {
  if (typeof navigator === "undefined" || typeof window === "undefined") {
    return "unknown";
  }
  const ua = navigator.userAgent || "";

  // Already installed?
  const standalone =
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true;
  if (standalone) return "installed";

  const isIPadOS = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  const isIOS = /iPad|iPhone|iPod/.test(ua) || isIPadOS;

  if (isIOS) {
    // In-app browsers — install impossible
    if (/FBAN|FBAV|Instagram|Line|Twitter|LinkedInApp|Pinterest/.test(ua)) return "ios_in_app";
    // iOS Chrome/Firefox/Edge — install impossible until user switches to Safari
    if (/CriOS|FxiOS|EdgiOS/.test(ua)) return "ios_other_browser";
    // Real iOS Safari — Share → Add to Home Screen works
    return "ios_safari";
  }

  // Android / Desktop — caller checks beforeinstallprompt separately.
  if (/Android/i.test(ua)) return "android";
  return "desktop";
}
